'use client';

import { c as _c } from "react/compiler-runtime";
import { decodeColor } from 'betterplace-utils';
import classNames from 'classnames';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { type CSSProperties, useMemo } from 'react';
import Amount from '../Amount';
import { composeSelectedFontObject, getDataCssVariables } from '../helpers';
import ScreenName from '../ScreenName';
import StreambotWrapper from '../StreambotWrapper';
import { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { LastCommentWidgetSettings } from './lastCommentWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import TrustedHtml from '@/components/TrustedHtml';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import { Locale } from '@/i18n/types';
export type LastCommentComponentData = {
  authorName: string | null;
  message: string | null;
  donatedAmountInCents: number | null;
};
type LastCommentProps = StreambotComponentProps<LastCommentWidgetSettings, LastCommentComponentData>;
const defaultDemoData = {
  authorName: 'rubber duck',
  message: 'I like this event',
  donatedAmountInCents: 5000
} satisfies LastCommentComponentData;
function useData(locale, fundraisingEventId, demoData) {
  const $ = _c(3);
  let t0;
  if ($[0] !== locale) {
    t0 = {
      locale
    };
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      selector(param) {
        if (!param?.data[0]) {
          return null;
        }
        const data = param.data[0];
        const returnedData = pick(data, ["author", "donated_amount_in_cents", "message"]);
        const authorName = returnedData.author?.name ?? null;
        const donatedAmountInCents = returnedData.donated_amount_in_cents ?? null;
        const message = returnedData.message;
        return {
          authorName,
          donatedAmountInCents,
          message
        };
      },
      intervalMs: 5000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/opinions.json?order=id:desc&per_page=1&page=1&facets=has_message:true`, t0, t1, demoData);
}
function LastComment(t0) {
  const $ = _c(78);
  let className;
  let fundraisingEventId;
  let props;
  let t1;
  let withDemoData;
  if ($[0] !== t0) {
    ({
      fundraisingEventId,
      demoData: t1,
      withDemoData,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fundraisingEventId;
    $[3] = props;
    $[4] = t1;
    $[5] = withDemoData;
  } else {
    className = $[1];
    fundraisingEventId = $[2];
    props = $[3];
    t1 = $[4];
    withDemoData = $[5];
  }
  const demoData = t1 === undefined ? defaultDemoData : t1;
  const locale = useLocale();
  const t = useTranslations("nextjs.streambot");
  let t2;
  if ($[6] !== props) {
    t2 = getDefaultValues(props);
    $[6] = props;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const mergedProps = t2;
  let commentFont;
  let commentFontColor;
  let commentFontSize;
  let commentFontWeight;
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let headlineText;
  let screenNameFont;
  let screenNameFontColor;
  let screenNameFontSize;
  let screenNameFontWeight;
  let wrapperProps;
  if ($[8] !== mergedProps) {
    ({
      screenNameFontColor,
      headlineText,
      headlineFont,
      headlineFontColor,
      headlineFontSize,
      headlineFontWeight,
      screenNameFont,
      screenNameFontSize,
      screenNameFontWeight,
      dataFont,
      dataFontColor,
      dataFontSize,
      dataFontWeight,
      commentFont,
      commentFontColor,
      commentFontSize,
      commentFontWeight,
      ...wrapperProps
    } = mergedProps);
    $[8] = mergedProps;
    $[9] = commentFont;
    $[10] = commentFontColor;
    $[11] = commentFontSize;
    $[12] = commentFontWeight;
    $[13] = dataFont;
    $[14] = dataFontColor;
    $[15] = dataFontSize;
    $[16] = dataFontWeight;
    $[17] = headlineFont;
    $[18] = headlineFontColor;
    $[19] = headlineFontSize;
    $[20] = headlineFontWeight;
    $[21] = headlineText;
    $[22] = screenNameFont;
    $[23] = screenNameFontColor;
    $[24] = screenNameFontSize;
    $[25] = screenNameFontWeight;
    $[26] = wrapperProps;
  } else {
    commentFont = $[9];
    commentFontColor = $[10];
    commentFontSize = $[11];
    commentFontWeight = $[12];
    dataFont = $[13];
    dataFontColor = $[14];
    dataFontSize = $[15];
    dataFontWeight = $[16];
    headlineFont = $[17];
    headlineFontColor = $[18];
    headlineFontSize = $[19];
    headlineFontWeight = $[20];
    headlineText = $[21];
    screenNameFont = $[22];
    screenNameFontColor = $[23];
    screenNameFontSize = $[24];
    screenNameFontWeight = $[25];
    wrapperProps = $[26];
  }
  const {
    data
  } = useData(locale, fundraisingEventId, withDemoData ? demoData : undefined);
  let t3;
  if ($[27] !== headlineFont || $[28] !== headlineFontWeight) {
    t3 = composeSelectedFontObject(headlineFont, headlineFontWeight);
    $[27] = headlineFont;
    $[28] = headlineFontWeight;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  let t4;
  if ($[30] !== screenNameFont || $[31] !== screenNameFontWeight) {
    t4 = composeSelectedFontObject(screenNameFont, screenNameFontWeight);
    $[30] = screenNameFont;
    $[31] = screenNameFontWeight;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== dataFont || $[34] !== dataFontWeight) {
    t5 = composeSelectedFontObject(dataFont, dataFontWeight);
    $[33] = dataFont;
    $[34] = dataFontWeight;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== commentFont || $[37] !== commentFontWeight) {
    t6 = composeSelectedFontObject(commentFont, commentFontWeight);
    $[36] = commentFont;
    $[37] = commentFontWeight;
    $[38] = t6;
  } else {
    t6 = $[38];
  }
  let t7;
  if ($[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t6) {
    t7 = [t3, t4, t5, t6];
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  const fonts = t7;
  let t8;
  if ($[44] !== fonts) {
    t8 = {
      fonts
    };
    $[44] = fonts;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  useGoogleFonts(t8);
  let t9;
  if ($[46] !== dataFont || $[47] !== dataFontColor || $[48] !== dataFontSize || $[49] !== dataFontWeight) {
    t9 = getDataCssVariables(dataFont, dataFontSize, dataFontWeight, dataFontColor);
    $[46] = dataFont;
    $[47] = dataFontColor;
    $[48] = dataFontSize;
    $[49] = dataFontWeight;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  const amountCssVariables = t9;
  let t10;
  if ($[51] !== className) {
    t10 = classNames("truncate-with-ellipsis", className);
    $[51] = className;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  let t11;
  if ($[53] !== headlineFont || $[54] !== headlineFontColor || $[55] !== headlineFontSize || $[56] !== headlineFontWeight || $[57] !== headlineText) {
    t11 = <WidgetHeadline content={headlineText} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontSize={headlineFontSize} headlineFontColor={headlineFontColor} />;
    $[53] = headlineFont;
    $[54] = headlineFontColor;
    $[55] = headlineFontSize;
    $[56] = headlineFontWeight;
    $[57] = headlineText;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  let t12;
  if ($[59] !== amountCssVariables || $[60] !== commentFont || $[61] !== commentFontColor || $[62] !== commentFontSize || $[63] !== commentFontWeight || $[64] !== data || $[65] !== screenNameFont || $[66] !== screenNameFontColor || $[67] !== screenNameFontSize || $[68] !== screenNameFontWeight || $[69] !== t) {
    t12 = data && data.message ? <><ScreenName authorName={data.authorName} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} /><Amount amount={data.donatedAmountInCents} showPlaceholder={true} className="donationData" style={amountCssVariables} /><TrustedHtml as="div" className="truncate-with-ellipsis donationComment" style={{
        "--streambot-comment-font": commentFont,
        "--streambot-comment-font-weight": commentFontWeight,
        "--streambot-comment-font-size": `${commentFontSize}px`,
        "--streambot-comment-font-color": decodeColor(commentFontColor?.toString())
      } as CSSProperties} value={data.message} /></> : <div style={amountCssVariables} className="donationData">{t("placeholder")}</div>;
    $[59] = amountCssVariables;
    $[60] = commentFont;
    $[61] = commentFontColor;
    $[62] = commentFontSize;
    $[63] = commentFontWeight;
    $[64] = data;
    $[65] = screenNameFont;
    $[66] = screenNameFontColor;
    $[67] = screenNameFontSize;
    $[68] = screenNameFontWeight;
    $[69] = t;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  let t13;
  if ($[71] !== t11 || $[72] !== t12) {
    t13 = <div>{t11}{t12}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== t10 || $[75] !== t13 || $[76] !== wrapperProps) {
    t14 = <StreambotWrapper {...wrapperProps} className={t10}>{t13}</StreambotWrapper>;
    $[74] = t10;
    $[75] = t13;
    $[76] = wrapperProps;
    $[77] = t14;
  } else {
    t14 = $[77];
  }
  return t14;
}
export default LastComment;